<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance, onMounted, type PropType } from 'vue';
import type { TranslatableValue } from "@/types/ui";

import LanguageDropdown from "@/components/elements/LanguageDropdown.vue";

const props = defineProps({
  modelValue: {
    type: [String, Number, Object] as PropType<string | number | TranslatableValue>,
    required: true,
  },
  translations: {
    type: Object,
    default: {},
  },
  type: {
    type: String,
    default: 'text',
  },
  label: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  unit: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  errorMessage: {
    type: String,
    default: '',
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  hasLang: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  fullW: {
    type: Boolean,
    default: false,
  },
  submitButton: {
    type: Boolean,
    default: false,
  },
  dark: {
    type: Boolean,
    default: false,
  },
  focus: {
    type: Boolean,
    default: false,
  },
  numberMin: {
    type: Number
  },
  numberMax: {
    type: Number
  },
  disableBlur: {
    type: Boolean,
    default: false,
  },
});
const instanceId = getCurrentInstance()?.uid || Math.floor(Math.random() * (99999 - 10000 + 1)) + 10000; // with fallback
const emit = defineEmits(["update:modelValue", "changeLang", "submit", "click", "onEsc"]);
const inputValue = ref(props.hasLang ? (props.modelValue as TranslatableValue).value : props.modelValue);
const langCode = ref(props.hasLang ? (props.modelValue as TranslatableValue).langCode : 'en');
const submitted = ref(true);

watch(
  [() => props.modelValue],
  () => {
  if (props.hasLang && (props.modelValue as TranslatableValue).value != inputValue.value) {
    inputValue.value = (props.modelValue as TranslatableValue).value;
    langCode.value = (props.modelValue as TranslatableValue).langCode;
  } else if (props.hasLang == false && props.modelValue != inputValue.value) {
    inputValue.value = props.modelValue;
  }
  },
  { deep: props.hasLang }
)

const mainClasses = computed(() => {
  let classList = "";
  classList += props.errorMessage || props.invalid ? 'form-item--error ' : '';
  classList += props.disabled ? 'form-item--disabled ' : '';
  classList += props.submitButton ? 'form-item--has-submit ' : '';
  classList += props.hasLang ? 'form-item--has-lang ' : '';
  classList += props.dark ? 'form-item--dark ' : '';
  classList += props.fullW ? 'form-item--full-w ' : '';
  classList += !props.label || props.label.length < 1 ? 'form-item--textfield--no-label' : '';
  classList += props.compact ? 'form-item--textfield--compact ' : '';
  classList += props.type == 'textarea' ? 'form-item--textfield--textarea ' : '';
  return classList;
});

function onInput() {
  submitted.value = false;
  if (props.hasLang) {
    emit("update:modelValue", {value: inputValue.value, langCode: langCode.value});
  } else {
    if (props.numberMin &&  inputValue.value.length > 0 && inputValue.value < props.numberMin) {
      inputValue.value = props.numberMin;
    } else if (props.numberMax && inputValue.value > props.numberMax) {
      inputValue.value = props.numberMax;
    }
    emit("update:modelValue", inputValue.value);
  }
};

function onBlur() {
  if (props.disableBlur || submitted.value) {
    return;
  }
  submit();
}

function setLanguage(code: string) {
  langCode.value = code;
  emit("changeLang", code);
}

function deleteText(code: string) {
  emit("update:modelValue", { value: '', langCode: code });
  emit("submit", { value: '', langCode: code });
}

function submit() {
  if (props.hasLang) {
    emit("submit", {value: inputValue.value, langCode: langCode.value});
  } else {
    emit("submit", inputValue.value);
  }
  submitted.value = true;
};

function clickInput() {
  emit("click");
}

function onEscape() {
  emit("onEsc");
}

onMounted(() => {
  if (props.focus) {
    const textField = document.getElementById(`textfield-${instanceId}`);
    if (textField) {
      textField.focus();
    }
  }
});

</script>
<template>
  <div class="form-item form-item--textfield" :class="mainClasses">
    <textarea 
      v-if="props.type == 'textarea'"
      v-model="inputValue"
      :type="props.type"
      :id="`textfield-${instanceId}`"
      @input="onInput"
      @blur="onBlur"
      @keyup.enter="submit"
      @keyup.esc="onEscape"
      @click.stop="clickInput"
      placeholder=" "
      :disabled="disabled"
      rows="3"></textarea>
    <input
      v-else
      v-model="inputValue"
      :value="inputValue"
      :type="props.type"
      :id="`textfield-${instanceId}`"
      @input="onInput"
      @blur="onBlur"
      @keyup.enter="submit"
      @keyup.esc="onEscape"
      @click.stop="clickInput"
      :disabled="disabled"
      :data-nofocus="!focus"
      placeholder=" ">
    <label :for="`textfield-${instanceId}`">{{ props.label }}</label>
    <span
      v-if="unit && unit.length > 0"
      :style="`left: ${16 + inputValue.toString().length * 10}px`"
      class="form-item--textfield__unit">
      {{ props.unit }}
    </span>
    <div v-if="hasLang" class="form-item__lang">
      <LanguageDropdown
        @set-lang="setLanguage"
        @delete-text="deleteText"
        :lang-code="langCode"
        :translations="translations" />
    </div>
    <button v-if="submitButton" @click.stop="submit" class="button button--secondary button--icon button--icon--medium form-item__submit">
      <i class="fa-regular fa-arrow-right"></i>
    </button>
    <p v-if="props.description" class="form-item__description">{{ props.description }}</p>
    <span v-show="props.errorMessage" class="form-item__error">{{ props.errorMessage }}</span>
  </div>
</template>
